import { Component, DestroyRef, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, filter, forkJoin, map, of, switchMap } from 'rxjs';
import { TabsModule } from 'primeng/tabs';
import { TranslatePipe } from '@ngx-translate/core';
import { SidebarMenu } from '../../_models/menu/SidebarMenu.model';
import { ConfigService } from '../../_services/config.service';
import { MenuHubContesto, MenuHubDef, MenuHubTab, buildMenuHubTabs, chiaveModulo, findMenuHub, moduliSchede, normMenuUrl } from '../../_services/menu-hub';
import { PortalService } from '../../portale/_services/portal.service';
import { isMenuUrlActive, menuRouterUrl } from '../app-sidebar-nav/app-sidebar-nav.component';

const CRUSCOTTO = '__cruscotto';
/** Classe su body nelle pagine principali delle schede: lì la freccia indietro della pagina è nascosta (styles.css). */
const CLASSE_PAGINA_SCHEDA = 'menu-hub-pagina-scheda';

/**
 * Schede di una sezione a schede (menu-hub.ts), mostrate nel layout sopra la pagina
 * quando la rotta corrente è il cruscotto o una delle pagine della sezione.
 */
@Component({
	selector: 'app-menu-hub-tabs',
	standalone: true,
	imports: [TabsModule, TranslatePipe],
	changeDetection: ChangeDetectionStrategy.Eager,
	template: `
		@if (hub) {
			<div class="kcs-card menu-hub-card">
				<div class="d-flex flex-row align-items-center">
					<button title="{{ 'Torna indietro' | translate }}" class="goBackButton menu-hub-back" (click)="esci()">
						<i class="pi pi-arrow-left" aria-hidden="true"></i>
					</button>
					<h5 class="titolo-card mb-0">{{ hub.label | translate }}</h5>
				</div>
				<p-tabs [value]="activeTabKey" [scrollable]="true">
					<p-tablist>
						<p-tab [value]="cruscottoKey" (click)="go(hub.homeUrl)"><i class="pi pi-chart-bar me-2"></i>{{ 'Cruscotto' | translate }}</p-tab>
						@for (tab of tabs; track tab.key) {
							<p-tab [value]="tab.key" (click)="go(tab.items[0].url)">{{ tab.label | translate }}</p-tab>
						}
					</p-tablist>
				</p-tabs>
				@if (activeTab && activeTab.items.length > 1) {
					<p-tabs [value]="activeItemUrl" [scrollable]="true" class="menu-hub-subtabs">
						<p-tablist>
							@for (item of activeTab.items; track item.url) {
								<p-tab [value]="item.url" (click)="go(item.url)">{{ item.label | translate }}</p-tab>
							}
						</p-tablist>
					</p-tabs>
				}
			</div>
		}
	`,
	styles: [
		`
			.menu-hub-card {
				padding-bottom: 0;
				margin-bottom: 1rem;
			}
			.menu-hub-subtabs {
				margin-top: 0.25rem;
				font-size: 13px;
			}
		`,
	],
})
export class MenuHubTabsComponent implements OnInit {
	private router = inject(Router);
	private portalService = inject(PortalService);
	private configService = inject(ConfigService);
	private destroyRef = inject(DestroyRef);

	readonly cruscottoKey = CRUSCOTTO;
	private menu: SidebarMenu[] = [];
	private hubs: MenuHubDef[] = [];

	hub: MenuHubDef | null = null;
	tabs: MenuHubTab[] = [];
	activeTabKey = '';
	activeTab: MenuHubTab | null = null;
	activeItemUrl = '';
	/** Ultima pagina fuori dalla sezione: la freccia indietro della sezione ci riporta lì, saltando le schede. */
	private uscita = '';
	private caricato = false;
	private contesto: MenuHubContesto = { cmpId: 0 };

	ngOnInit(): void {
		if (this.configService.getLocalStorageObjectJson('currentUser') == null) {
			return;
		}
		this.load();
		this.portalService.menuChanged$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.load());
		this.router.events
			.pipe(
				filter((e) => e instanceof NavigationEnd),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe(() => {
				this.refresh();
				if (this.caricato && !this.hub) {
					this.uscita = this.router.url;
				}
			});
	}

	private load(): void {
		const prt = this.configService.getPortalID();
		const cmp = this.configService.getCompany_id();
		forkJoin([this.portalService.getMenuItems(prt, cmp), this.portalService.getMenuHubs(prt, cmp)])
			.pipe(
				// funzioni dei moduli delle schede fuori menu (es. HR): stessa visibilita' delle card
				switchMap(([menu, hubKeys]) => {
					const moduli = moduliSchede(hubKeys || []);
					const funzioni$ = moduli.length ? forkJoin(moduli.map((m) => this.portalService.getFunzioniByUser(m).pipe(catchError(() => of([]))))) : of([] as any[][]);
					const nomi = (liste: any[][]) => [...liste.flat().map((f: any) => f?.mdfu_nome), ...moduli.filter((m, i) => liste[i]?.length).map(chiaveModulo)];
					return funzioni$.pipe(map((liste: any[][]) => ({ menu, hubKeys, funzioni: new Set<string>(nomi(liste)) })));
				}),
			)
			.subscribe(({ menu, hubKeys, funzioni }) => {
				this.menu = menu || [];
				this.hubs = (hubKeys || []).map(findMenuHub).filter((h): h is MenuHubDef => !!h);
				this.contesto = { cmpId: cmp, funzioni };
				this.caricato = true;
				this.refresh();
			});
	}

	/** Sezione e scheda attive per la rotta corrente: vince la pagina con l'url più lungo (più specifica). */
	private refresh(): void {
		this.trovaScheda();
		// pagina principale della scheda (la lista, non un dettaglio): niente freccia indietro della pagina,
		// che seguirebbe la cronologia delle schede; si esce dalla sezione con quella accanto al titolo
		const corrente = normMenuUrl(this.router.url);
		const base = this.activeTabKey === CRUSCOTTO ? normMenuUrl(this.hub?.homeUrl) : normMenuUrl(this.activeItemUrl);
		const principale = !!this.hub && [base, base + '/list', base + '/lista'].includes(corrente);
		document.body.classList.toggle(CLASSE_PAGINA_SCHEDA, principale);
	}

	private trovaScheda(): void {
		this.hub = null;
		this.activeTab = null;
		for (const hub of this.hubs) {
			const tabs = buildMenuHubTabs(this.menu, hub, this.contesto);
			// il cruscotto solo sul suo url esatto: per HR coincide con l'inizio di tutte le pagine HR
			if (normMenuUrl(this.router.url) === normMenuUrl(hub.homeUrl)) {
				this.hub = hub;
				this.tabs = tabs;
				this.activeTabKey = CRUSCOTTO;
				return;
			}
			let best: { tab: MenuHubTab; url: string; len: number } | null = null;
			for (const tab of tabs) {
				for (const item of tab.items) {
					const len = normMenuUrl(item.url).length;
					if (isMenuUrlActive(this.router, item.url) && (!best || len > best.len)) {
						best = { tab, url: item.url, len };
					}
				}
			}
			if (best) {
				this.hub = hub;
				this.tabs = tabs;
				this.activeTab = best.tab;
				this.activeTabKey = best.tab.key;
				this.activeItemUrl = best.url;
				return;
			}
		}
	}

	esci(): void {
		sessionStorage.clear();
		this.router.navigateByUrl(this.uscita || '/dashboard');
	}

	go(url: string): void {
		// già sulla pagina della scheda (non su un suo dettaglio): niente da fare
		if (!url || normMenuUrl(this.router.url) === normMenuUrl(url)) {
			return;
		}
		// come la sidebar: pulizia variabili di navigazione tra pagine diverse
		sessionStorage.clear();
		this.router.navigateByUrl(menuRouterUrl(url));
	}
}
